import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, Upload, Film, Package, Briefcase, CalendarDays, Store, Link2, BarChart3, Ticket, Layers } from "lucide-react";
import { sr } from "../../lib/sr";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPE_META = {
  movie: { label: "Movies", icon: Film, promo: "movies" },
  product: { label: "Products", icon: Package, promo: "products" },
  service: { label: "Services", icon: Briefcase, promo: "services" },
  event: { label: "Events", icon: CalendarDays, promo: "events" },
};
const money = (n, c) => (n == null ? "" : (c || "$") + Number(n).toLocaleString());

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((ln) => {
    const cells = ln.split(",");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    return row;
  });
}

export default function Catalog() {
  const [bm, setBm] = useState(null);
  const [type, setType] = useState(null);
  const [items, setItems] = useState(null);
  const [conn, setConn] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [form, setForm] = useState(null); // item being created/edited
  const [movie, setMovie] = useState(null); // movie item whose releases are open
  const [busy, setBusy] = useState(false);

  const types = () => {
    const pts = bm?.promote_types || [];
    const t = Object.keys(TYPE_META).filter((k) => pts.includes(TYPE_META[k].promo));
    return t.length ? t : ["product", "service", "movie", "event"];
  };
  const load = () => {
    setItems(null);
    sr.catalogItems(type ? { type } : {}).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  };
  useEffect(() => {
    sr.businessModel().then((b) => { setBm(b); const first = Object.keys(TYPE_META).find((k) => (b.promote_types || []).includes(TYPE_META[k].promo)); setType(first || null); }).catch(() => setBm({ promote_types: ["business"], catalog_label: "Catalog" }));
    sr.storeConnection().then((r) => { setConn(r.connection); setSyncEnabled(!!r.sync_enabled); }).catch(() => {});
  }, []);
  useEffect(() => { if (bm) load(); /* eslint-disable-next-line */ }, [type, bm]);

  const del = async (id) => { try { await sr.deleteItem(id); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); } };

  const showProducts = (bm?.promote_types || []).includes("products");
  const showCollections = (bm?.promote_types || []).some((p) => ["products", "services", "events", "business"].includes(p)) || !(bm?.promote_types || []).length;

  return (
    <div style={{ maxWidth: 1080 }} data-testid="catalog-page">
      <h1 style={{ fontSize: 24, color: "var(--navy)", margin: "0 0 4px" }} data-testid="catalog-heading">{bm?.catalog_label || "Catalog"}</h1>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 16 }}>Everything this client can promote. Reuse any item across multiple campaigns — or promote the whole business.</p>

      {showProducts && <StoreConnectCard conn={conn} setConn={setConn} syncEnabled={syncEnabled} onImported={load} />}

      {/* Type tabs */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {types().map((k) => {
          const M = TYPE_META[k]; const on = type === k;
          return (
            <button key={k} className={`sr-btn sr-btn-sm ${on ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setType(k)} data-testid={`catalog-tab-${k}`}>
              <M.icon size={14} /> {M.label}
            </button>
          );
        })}
        {showCollections && (
          <button className={`sr-btn sr-btn-sm ${type === "collections" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setType("collections")} data-testid="catalog-tab-collections">
            <Layers size={14} /> Collections
          </button>
        )}
      </div>

      {type === "collections" ? <CollectionsManager showProducts={showProducts} /> : (<>
      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <button className="sr-btn sr-btn-primary" onClick={() => setForm({ item_type: type || "product", status: "active", currency: "USD", details: {} })} data-testid="catalog-add-item">
          <Plus size={15} /> Add {TYPE_META[type]?.label?.replace(/s$/, "") || "item"}
        </button>
        <CsvImport type={type || "product"} onDone={load} />
      </div>

      {items === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        : items.length === 0 ? <div className="sr-empty" data-testid="catalog-empty">No {TYPE_META[type]?.label?.toLowerCase() || "items"} yet. Add one, import a CSV{showProducts ? ", or connect your store" : ""}.</div>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
            {items.map((it) => (
              <div key={it.id} className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid={`catalog-item-${it.id}`}>
                <div style={{ height: 120, background: it.images?.[0] ? `center/cover no-repeat url(${it.images[0]})` : "#EEF2F7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {!it.images?.[0] && React.createElement(TYPE_META[it.item_type]?.icon || Package, { size: 28, color: "#9AA7B8" })}
                </div>
                <div style={{ padding: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <strong style={{ flex: 1, minWidth: 0, color: "var(--navy)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</strong>
                    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 999, padding: "1px 8px", background: it.status === "active" ? "#DCFCE7" : it.status === "archived" ? "#F1F5F9" : "#FEF3C7", color: it.status === "active" ? "#166534" : "#64748B" }}>{it.status}</span>
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12, margin: "4px 0", minHeight: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.short_description || it.category || ""}</div>
                  {it.price != null && <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13 }}>{money(it.price, it.currency === "USD" ? "$" : (it.currency ? it.currency + " " : "$"))}</div>}
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    {it.item_type === "movie" && <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => setMovie(it)} data-testid={`catalog-releases-${it.id}`}><Film size={13} /> Releases</button>}
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm(it)} data-testid={`catalog-edit-${it.id}`}><Pencil size={13} /></button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => del(it.id)} data-testid={`catalog-delete-${it.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}

      {form && <ItemForm form={form} setForm={setForm} busy={busy} setBusy={setBusy} onSaved={() => { setForm(null); load(); }} />}
      {movie && <MovieManager item={movie} onClose={() => setMovie(null)} />}
    </div>
  );
}

function StoreConnectCard({ conn, setConn, syncEnabled, onImported }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const status = conn?.status || "not_connected";
  const connected = status !== "not_connected";
  const stats = conn?.sync_stats;
  const save = async () => {
    setBusy(true);
    try { const r = await sr.connectStore({ provider: "shopify", shop_domain: domain, access_token: token }); setConn(r.connection); toast.message(r.message || "Saved"); setOpen(false); setToken(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save connection"); } finally { setBusy(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const r = await sr.syncStore();
      toast.message(r.message || "Sync requested");
      const c = await sr.storeConnection(); setConn(c.connection);
      if (r.ok) onImported && onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setSyncing(false); }
  };
  const disconnect = async () => { try { await sr.disconnectStore(); const r = await sr.storeConnection(); setConn(r.connection); toast.success("Disconnected"); } catch { toast.error("Could not disconnect"); } };
  const STATUS_LABEL = { not_connected: "Not connected", setup_required: "Setup required", connected: "Connected", syncing: "Syncing", ready: "Ready", degraded: "Degraded", unsupported: "Unsupported" };
  return (
    <div className="sr-card" style={{ marginBottom: 16, borderLeft: "3px solid #95BF47" }} data-testid="store-connect-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Store size={18} style={{ color: "#5E8E3E" }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <strong style={{ color: "var(--navy)" }}>Shopify store</strong>
          <div className="sr-muted" style={{ fontSize: 12 }}>Sync products, inventory & verified orders. {connected ? conn?.shop_domain : "WooCommerce coming next."}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 10px", background: connected ? "#FEF3C7" : "#F1F5F9", color: connected ? "#92400E" : "#64748B" }} data-testid="store-status">{STATUS_LABEL[status] || status}</span>
        {!connected ? <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setOpen((v) => !v)} data-testid="store-connect-btn">Connect</button>
          : <><button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={sync} disabled={syncing} data-testid="store-sync-btn">{syncing ? <Loader2 size={13} className="sr-spin" /> : "Sync now"}</button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={disconnect} data-testid="store-disconnect-btn">Disconnect</button></>}
      </div>
      {connected && conn?.error && <p style={{ fontSize: 11.5, marginTop: 8, color: "#B91C1C" }} data-testid="store-error">{conn.error}</p>}
      {connected && stats && (
        <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 8 }} data-testid="store-sync-stats">
          Last sync imported <strong>{stats.products}</strong> products and <strong>{stats.orders}</strong> orders{stats.shop_name ? ` from ${stats.shop_name}` : ""}.{conn?.last_sync ? ` · ${new Date(conn.last_sync).toLocaleString()}` : ""}
        </p>
      )}
      {connected && !stats && (
        <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 8 }}>
          {syncEnabled ? "Credentials saved securely. Press \u201CSync now\u201D to import products & orders." : "Credentials saved securely. Live Shopify sync isn\u2019t enabled on this server yet \u2014 import via CSV or add products manually for now."}
        </p>
      )}
      {open && !connected && (
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          <input className="sr-input" placeholder="your-store.myshopify.com" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="store-domain" />
          <input className="sr-input" placeholder="Admin API access token" value={token} onChange={(e) => setToken(e.target.value)} type="password" data-testid="store-token" />
          <div style={{ display: "flex", gap: 8 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={busy} data-testid="store-save">{busy ? <Loader2 size={13} className="sr-spin" /> : "Save connection"}</button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
          <p className="sr-muted" style={{ fontSize: 11 }}>Your token is stored securely and never shown again. {syncEnabled ? "We'll verify it and sync on demand." : "We'll wire live sync when it's enabled."}</p>
        </div>
      )}
    </div>
  );
}

function CsvImport({ type, onDone }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const rows = parseCsv(await f.text());
      if (!rows.length) { toast.error("No rows found — need a header row + data"); return; }
      const r = await sr.importItemsCsv(type, rows);
      toast.success(`Imported ${r.created} item(s)`); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="sr-btn sr-btn-ghost" style={{ cursor: "pointer" }} data-testid="catalog-csv-import">
      {busy ? <Loader2 size={15} className="sr-spin" /> : <Upload size={15} />} Import CSV
      <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: "none" }} />
    </label>
  );
}

function ItemForm({ form, setForm, busy, setBusy, onSaved }) {
  const isNew = !form.id;
  const d = form.details || {};
  const setD = (k, v) => setForm({ ...form, details: { ...(form.details || {}), [k]: v } });
  const t = form.item_type;
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = { ...form, territories: typeof form.territories === "string" ? form.territories.split(",").map((s) => s.trim()).filter(Boolean) : form.territories,
      images: typeof form.images === "string" ? [form.images].filter(Boolean) : form.images, price: form.price === "" ? null : (form.price != null ? Number(form.price) : null) };
    try { if (isNew) await sr.createItem(body); else await sr.updateItem(form.id, body); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="sr-modal-overlay" onClick={() => setForm(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}>
      <div className="sr-modal" style={{ maxWidth: 560, width: "100%", background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.3)", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="catalog-item-form">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong style={{ color: "var(--navy)", fontSize: 16 }}>{isNew ? "Add" : "Edit"} {TYPE_META[t]?.label?.replace(/s$/, "") || "item"}</strong>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm(null)}><X size={16} /></button>
        </div>
        <div style={{ display: "grid", gap: 10, maxHeight: "64vh", overflowY: "auto" }}>
          <div className="sr-field"><label className="sr-label">Name</label><input className="sr-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></div>
          <div className="sr-field"><label className="sr-label">Short description</label><input className="sr-input" value={form.short_description || ""} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="item-short-desc" /></div>
          <div className="sr-field"><label className="sr-label">{t === "movie" ? "Synopsis" : "Full description"}</label><textarea className="sr-input" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="sr-field"><label className="sr-label">Category</label><input className="sr-input" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="sr-field"><label className="sr-label">Status</label>
              <select className="sr-select" value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="item-status">
                <option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option>
              </select></div>
          </div>
          <div className="sr-field"><label className="sr-label">Image URL</label><input className="sr-input" value={Array.isArray(form.images) ? (form.images[0] || "") : (form.images || "")} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="https://…" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="sr-field"><label className="sr-label">Destination URL / CTA link</label><input className="sr-input" value={form.destination_url || ""} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} placeholder="https://…" data-testid="item-dest" /></div>
            <div className="sr-field"><label className="sr-label">CTA text</label><input className="sr-input" value={form.cta || ""} onChange={(e) => setForm({ ...form, cta: e.target.value })} placeholder="Shop now / Book / Get tickets" /></div>
          </div>
          {(t === "product" || t === "service" || t === "event") && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="sr-field"><label className="sr-label">Price</label><input className="sr-input" type="number" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="item-price" /></div>
              <div className="sr-field"><label className="sr-label">Currency</label><input className="sr-input" value={form.currency || "USD"} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            </div>
          )}
          <div className="sr-field"><label className="sr-label">Available in (territories, comma-separated)</label><input className="sr-input" value={Array.isArray(form.territories) ? form.territories.join(", ") : (form.territories || "")} onChange={(e) => setForm({ ...form, territories: e.target.value })} placeholder="United States, Canada" /></div>

          {t === "movie" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="sr-field"><label className="sr-label">Genre</label><input className="sr-input" value={d.genre || ""} onChange={(e) => setD("genre", e.target.value)} /></div>
              <div className="sr-field"><label className="sr-label">Language</label><input className="sr-input" value={d.language || ""} onChange={(e) => setD("language", e.target.value)} /></div>
              <div className="sr-field"><label className="sr-label">Runtime</label><input className="sr-input" value={d.runtime || ""} onChange={(e) => setD("runtime", e.target.value)} placeholder="128 min" /></div>
              <div className="sr-field"><label className="sr-label">Rating</label><input className="sr-input" value={d.rating || ""} onChange={(e) => setD("rating", e.target.value)} placeholder="PG-13" /></div>
              <div className="sr-field"><label className="sr-label">IMDB link</label><input className="sr-input" value={d.imdb_url || ""} onChange={(e) => setD("imdb_url", e.target.value)} placeholder="https://imdb.com/title/…" data-testid="item-imdb" /></div>
              <div className="sr-field"><label className="sr-label">YouTube trailer URL</label><input className="sr-input" value={d.trailer_url || ""} onChange={(e) => setD("trailer_url", e.target.value)} placeholder="https://youtube.com/watch?v=…" data-testid="item-trailer" /></div>
            </div>
          )}
          {t === "product" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="sr-field"><label className="sr-label">SKU</label><input className="sr-input" value={d.sku || ""} onChange={(e) => setD("sku", e.target.value)} /></div>
              <div className="sr-field"><label className="sr-label">Brand</label><input className="sr-input" value={d.brand || ""} onChange={(e) => setD("brand", e.target.value)} /></div>
            </div>
          )}
          {t === "service" && (
            <div className="sr-field"><label className="sr-label">Pricing</label>
              <select className="sr-select" value={d.pricing_type || "one_time"} onChange={(e) => setD("pricing_type", e.target.value)}>
                <option value="one_time">One-time</option><option value="recurring">Recurring / subscription</option>
              </select></div>
          )}
          {t === "event" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="sr-field"><label className="sr-label">Event date</label><input className="sr-input" type="date" value={d.event_date || ""} onChange={(e) => setD("event_date", e.target.value)} /></div>
              <div className="sr-field"><label className="sr-label">Venue</label><input className="sr-input" value={d.venue || ""} onChange={(e) => setD("venue", e.target.value)} /></div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="sr-btn sr-btn-primary" onClick={save} disabled={busy} data-testid="item-save">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save"}</button>
          <button className="sr-btn sr-btn-ghost" onClick={() => setForm(null)}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function MovieManager({ item, onClose }) {
  const [releases, setReleases] = useState(null);
  const [stats, setStats] = useState(null);
  const [rForm, setRForm] = useState(null);
  const [active, setActive] = useState(null); // release whose theaters are open
  const load = () => sr.releases(item.id).then((r) => setReleases(r.releases || [])).catch(() => setReleases([]));
  useEffect(() => { load(); sr.itemStats(item.id).then((r) => setStats(r.stats)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const saveRelease = async () => {
    if (!rForm.territory?.trim()) { toast.error("Territory is required"); return; }
    try {
      const body = { ...rForm, markets: typeof rForm.markets === "string" ? rForm.markets.split(",").map((s) => s.trim()).filter(Boolean) : rForm.markets, budget_total: rForm.budget_total ? Number(rForm.budget_total) : null };
      if (rForm.id) await sr.updateRelease(rForm.id, body); else await sr.createRelease(item.id, body);
      toast.success("Release saved"); setRForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save release"); }
  };
  const delRelease = async (rid) => { try { await sr.deleteRelease(rid); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); } };
  return (
    <div className="sr-modal-overlay" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}>
      <div className="sr-modal" style={{ maxWidth: 680, width: "100%", background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.3)", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="movie-manager">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <strong style={{ color: "var(--navy)", fontSize: 16 }}><Film size={16} /> {item.name} — releases</strong>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {stats && (
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--muted)", marginBottom: 10 }} data-testid="movie-stats">
            <span><BarChart3 size={12} /> {stats.movie_page_view} page views</span>
            <span>{stats.trailer_view} trailer views</span>
            <span><Ticket size={12} /> {stats.ticket_link_click} ticket-link clicks</span>
            <span className="sr-muted">(clicks ≠ ticket sales)</span>
          </div>
        )}
        <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginBottom: 10 }} onClick={() => setRForm({ release_type: "theatrical", status: "planned" })} data-testid="add-release"><Plus size={13} /> Add release</button>

        {releases === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
          : releases.length === 0 ? <div className="sr-empty">No releases yet — add the first territory & date.</div>
          : (
            <div style={{ display: "grid", gap: 8 }}>
              {releases.map((r) => (
                <div key={r.id} className="sr-card" data-testid={`release-${r.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: "var(--navy)" }}>{r.territory || "—"} · {r.release_type}</strong>
                      <div className="sr-muted" style={{ fontSize: 12 }}>{r.release_date || "no date"}{r.markets?.length ? ` · ${r.markets.length} markets` : ""}{r.budget_total ? ` · ${money(r.budget_total, "$")}` : ""}</div>
                    </div>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setActive(active?.id === r.id ? null : r)} data-testid={`release-theaters-${r.id}`}>Theaters</button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setRForm(r)}><Pencil size={13} /></button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => delRelease(r.id)}><Trash2 size={13} /></button>
                  </div>
                  {active?.id === r.id && <TheaterPanel release={r} />}
                </div>
              ))}
            </div>
          )}

        {rForm && (
          <div className="sr-card" style={{ marginTop: 12, border: "1px solid var(--blue)" }} data-testid="release-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="sr-field"><label className="sr-label">Territory / country</label><input className="sr-input" value={rForm.territory || ""} onChange={(e) => setRForm({ ...rForm, territory: e.target.value })} placeholder="United States" data-testid="release-territory" /></div>
              <div className="sr-field"><label className="sr-label">Type</label>
                <select className="sr-select" value={rForm.release_type || "theatrical"} onChange={(e) => setRForm({ ...rForm, release_type: e.target.value })}>
                  <option value="theatrical">Theatrical</option><option value="streaming">Streaming</option><option value="other">Other</option>
                </select></div>
              <div className="sr-field"><label className="sr-label">Release date</label><input className="sr-input" type="date" value={rForm.release_date || ""} onChange={(e) => setRForm({ ...rForm, release_date: e.target.value })} data-testid="release-date" /></div>
              <div className="sr-field"><label className="sr-label">Total budget</label><input className="sr-input" type="number" value={rForm.budget_total ?? ""} onChange={(e) => setRForm({ ...rForm, budget_total: e.target.value })} /></div>
            </div>
            <div className="sr-field"><label className="sr-label">Markets / cities (comma-separated)</label><input className="sr-input" value={Array.isArray(rForm.markets) ? rForm.markets.join(", ") : (rForm.markets || "")} onChange={(e) => setRForm({ ...rForm, markets: e.target.value })} placeholder="Dallas, Houston, Chicago" /></div>
            <div className="sr-field"><label className="sr-label">Release landing page</label><input className="sr-input" value={rForm.landing_url || ""} onChange={(e) => setRForm({ ...rForm, landing_url: e.target.value })} placeholder="https://…" /></div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveRelease} data-testid="release-save">Save release</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setRForm(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TheaterPanel({ release }) {
  const [theaters, setTheaters] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => sr.theaters(release.id).then((r) => setTheaters(r.theaters || [])).catch(() => setTheaters([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const save = async () => { if (!form.name?.trim()) { toast.error("Theater name required"); return; } try { await sr.createTheater(release.id, form); toast.success("Added"); setForm(null); load(); } catch { toast.error("Could not add"); } };
  const del = async (id) => { try { await sr.deleteTheater(id); load(); } catch { toast.error("Could not remove"); } };
  const onCsv = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const rows = parseCsv(await f.text()); const r = await sr.importTheatersCsv(release.id, rows); toast.success(`Imported ${r.created} theaters`); load(); } catch { toast.error("Import failed"); } e.target.value = ""; };
  const ticketUrl = (t) => `${BACKEND}/api/t/${release.id}/${t.id}`;
  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid={`theater-panel-${release.id}`}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm({ source: "manual" })} data-testid="add-theater"><Plus size={12} /> Add theater</button>
        <label className="sr-btn sr-btn-ghost sr-btn-sm" style={{ cursor: "pointer" }} data-testid="theater-csv"><Upload size={12} /> CSV<input type="file" accept=".csv" onChange={onCsv} style={{ display: "none" }} /></label>
      </div>
      {theaters === null ? <Loader2 className="sr-spin" size={14} />
        : theaters.length === 0 ? <div className="sr-muted" style={{ fontSize: 12 }}>No theaters / ticket destinations yet.</div>
        : theaters.map((t) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "4px 0" }} data-testid={`theater-${t.id}`}>
            <span style={{ flex: 1, color: "var(--navy)" }}><strong>{t.name}</strong>{t.market ? ` · ${t.market}` : ""} {t.source === "csv" && <em className="sr-muted">(imported)</em>}</span>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" title="Copy tracked ticket link" onClick={() => { navigator.clipboard?.writeText(ticketUrl(t)); toast.success("Tracked ticket link copied"); }}><Link2 size={12} /></button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => del(t.id)}><Trash2 size={12} /></button>
          </div>
        ))}
      {form && (
        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
          <input className="sr-input" placeholder="Cinema / venue name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="theater-name" />
          <div style={{ display: "flex", gap: 6 }}>
            <input className="sr-input" placeholder="Market / city" value={form.market || ""} onChange={(e) => setForm({ ...form, market: e.target.value })} />
            <input className="sr-input" placeholder="Ticket booking URL" value={form.ticket_url || ""} onChange={(e) => setForm({ ...form, ticket_url: e.target.value })} data-testid="theater-url" />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} data-testid="theater-save">Add</button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CollectionsManager({ showProducts }) {
  const [cols, setCols] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => sr.collections().then((r) => setCols(r.collections || [])).catch(() => setCols([]));
  useEffect(() => { load(); sr.catalogItems({}).then((r) => setItems(r.items || [])).catch(() => {}); }, []);
  const del = async (id) => { try { await sr.deleteCollection(id); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); } };
  const itemName = (id) => items.find((i) => i.id === id)?.name || "item";
  return (
    <div data-testid="collections-manager">
      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <button className="sr-btn sr-btn-primary" onClick={() => setForm({ name: "", description: "", item_ids: [] })} data-testid="collection-add"><Plus size={15} /> New collection</button>
      </div>
      {cols === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        : cols.length === 0 ? <div className="sr-empty" data-testid="collections-empty">No collections yet. Group related {showProducts ? "products" : "items"} into a collection to promote them together in one campaign.</div>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
            {cols.map((c) => (
              <div key={c.id} className="sr-card" data-testid={`collection-${c.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Layers size={16} style={{ color: "var(--blue)" }} />
                  <strong style={{ flex: 1, color: "var(--navy)", fontSize: 15 }}>{c.name}</strong>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm({ ...c })} data-testid={`collection-edit-${c.id}`}><Pencil size={13} /></button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => del(c.id)} data-testid={`collection-delete-${c.id}`}><Trash2 size={13} /></button>
                </div>
                {c.description && <div className="sr-muted" style={{ fontSize: 12, margin: "6px 0" }}>{c.description}</div>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 8px", background: "#EEF2F7", color: "var(--navy)" }}>{(c.item_ids || []).length} items</span>
                  {(c.item_ids || []).slice(0, 4).map((id) => <span key={id} className="sr-muted" style={{ fontSize: 11 }}>{itemName(id)}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      {form && <CollectionForm form={form} setForm={setForm} items={items} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function CollectionForm({ form, setForm, items, onSaved }) {
  const [busy, setBusy] = useState(false);
  const isNew = !form.id;
  const sel = form.item_ids || [];
  const toggle = (id) => setForm({ ...form, item_ids: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = { name: form.name, description: form.description, item_ids: sel };
      if (isNew) await sr.createCollection(body); else await sr.updateCollection(form.id, body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="sr-modal-overlay" onClick={() => setForm(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}>
      <div className="sr-modal" style={{ maxWidth: 520, width: "100%", background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.3)", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="collection-form">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong style={{ color: "var(--navy)", fontSize: 16 }}>{isNew ? "New" : "Edit"} collection</strong>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm(null)}><X size={16} /></button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="sr-field"><label className="sr-label">Name</label><input className="sr-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="collection-name" placeholder="Summer Collection / New Releases" /></div>
          <div className="sr-field"><label className="sr-label">Description</label><input className="sr-input" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="sr-field">
            <label className="sr-label">Items in this collection ({sel.length})</label>
            {items.length === 0 ? <p className="sr-muted" style={{ fontSize: 12 }}>No catalog items yet — add some first.</p>
              : (
                <div style={{ display: "grid", gap: 6, maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 8 }}>
                  {items.map((it) => {
                    const on = sel.includes(it.id);
                    return (
                      <label key={it.id} data-testid={`collection-item-${it.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--navy)", cursor: "pointer", padding: "5px 6px", border: "1px solid " + (on ? "var(--blue)" : "transparent"), borderRadius: 6 }}>
                        <input type="checkbox" checked={on} onChange={() => toggle(it.id)} />
                        <span style={{ flex: 1 }}>{it.name}</span>
                        <span className="sr-muted" style={{ fontSize: 11 }}>{it.item_type}</span>
                      </label>
                    );
                  })}
                </div>
              )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="sr-btn sr-btn-primary" onClick={save} disabled={busy} data-testid="collection-save">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save"}</button>
          <button className="sr-btn sr-btn-ghost" onClick={() => setForm(null)}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

